import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Ticket, Star, TrendingUp, Radar as RadarIcon, Activity, Lock, Check, Shuffle } from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PurchaseButton from "@/components/PurchaseButton";
import { pickModuleAccess } from "@/lib/moduleAccess";

export default function ParentPerformance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [perf, setPerf] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [mixedBusy, setMixedBusy] = useState(false);
  const [credits, setCredits] = useState(0);
  // Abonelik (unlimited/aylık kota) varsa AI Analizi kredi istemeden açılır — kredi sadece
  // abonelik yoksa veya kota tükendiğinde devreye giren bir fallback.
  const [aiAccess, setAiAccess] = useState({ free: false, cost: 2 });
  const [mixedAccess, setMixedAccess] = useState({ free: false, cost: 3 });
  // null = still checking; only relevant for role="parent" — Çocuk Raporu (child report) is a
  // paid-only page for parents, gated behind the "rapor" plan (ai_rapor unlimited feature).
  const [entitled, setEntitled] = useState(user?.role === "student");
  const [checkingAccess, setCheckingAccess] = useState(user?.role !== "student");
  const [reportPlan, setReportPlan] = useState(null);

  const loadCredits = () =>
    api.get(`${V1}/users/me/credits`).then((r) => setCredits(r.data?.exam_credits ?? 0)).catch(() => {});

  useEffect(() => {
    if (user?.role !== "parent") { setEntitled(true); setCheckingAccess(false); return; }
    setCheckingAccess(true);
    Promise.all([
      api.get(`${V1}/users/me/subscription`),
      api.get(`${V1}/subscription-plans`, { params: { audience: "parent" } }),
    ]).then(([subRes, plansRes]) => {
      const hasReport = !!subRes.data?.plan?.features?.ai_rapor?.unlimited;
      setEntitled(hasReport);
      setReportPlan((plansRes.data || []).find((p) => p.code === "rapor") || null);
    }).catch(() => setEntitled(false))
      .finally(() => setCheckingAccess(false));
  }, [user?.role]);

  useEffect(() => {
    if (!entitled) return;
    api.get(`${V1}/performance/me`).then((r) => setPerf(r.data));
    api.get(`${V1}/exam-sessions/my`).then((r) => setSessions(r.data || []));
    api.get(`${V1}/users/me/usage`).then((r) => setAiAccess(pickModuleAccess(r.data, "ai_rapor"))).catch(() => {});
    api.get(`${V1}/users/me/usage`).then((r) => setMixedAccess(pickModuleAccess(r.data, "premium_denemeler"))).catch(() => {});
    loadCredits();
  }, [entitled]);

  const startMixedExam = async () => {
    if (!mixedAccess.free && credits < mixedAccess.cost) {
      toast.error(`AI karma deneme ${mixedAccess.cost} kredi. Bakiyeniz: ${credits}`);
      return;
    }
    setMixedBusy(true);
    try {
      const { data: exam } = await api.post(`${V1}/ai/mixed-exam`, {});
      const { data: session } = await api.post(`${V1}/exam-sessions/start`, { mock_exam_id: exam.id });
      toast.success(exam.title || "Karma deneme hazır!");
      navigate(`/sinav/${session.session_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Karma deneme oluşturulamadı");
    } finally { setMixedBusy(false); }
  };

  const genSummary = async () => {
    if (!aiAccess.free && credits < aiAccess.cost) {
      toast.error(`AI raporu ${aiAccess.cost} kredi. Bakiyeniz: ${credits}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`${V1}/ai/performance-summary`, {});
      setSummary(data.content);
      if (aiAccess.free) {
        toast.success("AI raporu hazır. Aboneliğin kapsamında ücretsiz.");
        api.get(`${V1}/users/me/usage`).then((r) => setAiAccess(pickModuleAccess(r.data, "ai_rapor"))).catch(() => {});
      } else {
        loadCredits();
        toast.success(`AI raporu hazır. ${aiAccess.cost} kredi kullanıldı.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Özet oluşturulamadı");
    } finally { setBusy(false); }
  };

  const stars = perf?.star_rating || 0;
  const radar = perf?.subject_radar || [];
  const weekly = perf?.weekly_progress || [];

  if (checkingAccess) return <div className="p-16 text-center text-muted-foreground">Yükleniyor…</div>;

  if (!entitled) {
    return (
      <div className="max-w-2xl mx-auto py-8 text-center" data-testid="performance-locked">
        <div className="p-8 rounded-2xl border-2 border-dashed border-accent/40 bg-accent/5">
          <Lock className="h-12 w-12 mx-auto text-accent mb-3" />
          <h1 className="font-display text-2xl font-bold">Çocuk Raporu Kilitli</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Radar grafik, haftalık ilerleme, ders ortalamaları ve AI analizini görmek için
            Rapor planına geçmen gerekiyor.
          </p>
          {reportPlan && (
            <div className="grid grid-cols-2 gap-3 mt-6 max-w-xs mx-auto">
              <div className="p-4 rounded-lg border border-border bg-card">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Aylık</div>
                <div className="text-2xl font-display font-bold">₺{reportPlan.price_monthly}</div>
              </div>
              <div className="p-4 rounded-lg border border-accent/40 bg-accent/10">
                <div className="text-xs uppercase tracking-widest text-accent">Yıllık</div>
                <div className="text-2xl font-display font-bold">₺{reportPlan.price_yearly}</div>
                <div className="text-[10px] text-accent">2 ay bedava</div>
              </div>
            </div>
          )}
          <div className="mt-4 text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-success" /> Sınırsız Çocuk Raporu erişimi
          </div>
          {reportPlan && (
            <div className="mt-6 flex gap-2 justify-center flex-wrap">
              <PurchaseButton kind="plan" code="rapor" cycle="monthly" price={reportPlan.price_monthly} label={`Aylık ₺${reportPlan.price_monthly} · Rapor Planına Geç`} testId="report-upgrade-monthly" />
              <PurchaseButton kind="plan" code="rapor" cycle="yearly" price={reportPlan.price_yearly} label={`Yıllık ₺${reportPlan.price_yearly}`} variant="outline" testId="report-upgrade-yearly" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Performans</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Sınav Performansı</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/panel/referans" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm" data-testid="perf-credits-chip">
            <Ticket className="h-3 w-3" /> {credits} Kredi
          </Link>
          <Button onClick={genSummary} disabled={busy || (!aiAccess.free && credits < aiAccess.cost)} data-testid="ai-summary-btn">
            <Sparkles className="h-4 w-4" />
            {busy ? "Analiz ediliyor…" : aiAccess.free ? "AI Analizi Oluştur (Aboneliğine Dahil)" : `AI Analizi (${aiAccess.cost} Kredi)`}
          </Button>
          <Button
            variant="outline"
            onClick={startMixedExam}
            disabled={mixedBusy || (!mixedAccess.free && credits < mixedAccess.cost)}
            data-testid="ai-mixed-exam-btn"
          >
            <Shuffle className="h-4 w-4" />
            {mixedBusy ? "Hazırlanıyor…" : mixedAccess.free ? "AI Karma Deneme (Aboneliğine Dahil)" : `AI Karma Deneme (${mixedAccess.cost} Kredi)`}
          </Button>
        </div>
      </div>

      {/* Hero: overall rating card */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-accent/5 p-6" data-testid="perf-hero">
        <div className="flex items-center justify-between flex-wrap gap-6">
          <div>
            <div className="overline text-accent mb-1">Genel Değerlendirme</div>
            <div className="flex items-baseline gap-3">
              <div className="font-display text-6xl font-bold tracking-tight">%{perf?.overall_average ?? 0}</div>
              <div className="text-sm text-muted-foreground">ortalama</div>
            </div>
            <div className="flex items-center gap-1 mt-3" data-testid="perf-star-rating" aria-label={`${stars} yıldız`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`h-6 w-6 transition-transform ${n <= stars ? "fill-amber-400 text-amber-400 scale-110" : "text-muted stroke-muted-foreground"}`}
                  style={{ transitionDelay: `${n * 60}ms` }}
                  data-testid={`perf-star-${n}`}
                />
              ))}
              <span className="text-xs text-muted-foreground ml-2">{stars}/5</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 min-w-[280px]">
            <MiniStat icon={<Activity className="h-4 w-4" />} value={perf?.exams_completed ?? 0} label="Tamamlanan" />
            <MiniStat icon={<TrendingUp className="h-4 w-4" />} value={`%${perf?.attendance_rate ?? 0}`} label="Katılım" />
            <MiniStat icon={<Ticket className="h-4 w-4" />} value={perf?.exams_joined ?? 0} label="Aktif" />
          </div>
        </div>
      </div>

      {summary && (
        <div className="p-6 rounded-xl border border-accent/30 bg-accent/5" data-testid="ai-summary-block">
          <div className="overline text-accent mb-2 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Analizi</div>
          <div className="text-sm whitespace-pre-line leading-relaxed">{summary}</div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Radar chart */}
        <div className="p-6 rounded-xl border border-border bg-card" data-testid="perf-radar-card">
          <div className="font-display font-bold text-lg mb-2 flex items-center gap-2">
            <RadarIcon className="h-5 w-5 text-primary" /> Ders Bazlı Radar
          </div>
          <div className="text-xs text-muted-foreground mb-4">Her dersteki güçlü ve zayıf yönlerini görsel olarak gör.</div>
          {radar.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
              Radar için en az bir sınav tamamlaman gerekli.
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radar} outerRadius="80%">
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                  <Radar name="Ortalama" dataKey="value" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.35} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Weekly progress */}
        <div className="p-6 rounded-xl border border-border bg-card" data-testid="perf-weekly-card">
          <div className="font-display font-bold text-lg mb-2 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" /> Haftalık İlerleme
          </div>
          <div className="text-xs text-muted-foreground mb-4">Son 8 haftadaki başarı yüzdesi trendi.</div>
          {weekly.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
              Haftalık trend için birkaç sınav çözmen gerekli.
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weekly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    formatter={(v, key) => key === "average" ? [`%${v}`, "Ortalama"] : [v, "Net"]} />
                  <Legend />
                  <Line type="monotone" dataKey="average" name="Ortalama (%)" stroke="hsl(var(--accent))" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Subject averages bar list */}
      {perf?.subject_averages && Object.keys(perf.subject_averages).length > 0 && (
        <div className="p-6 rounded-xl border border-border bg-card">
          <div className="font-display font-bold text-lg mb-4">Ders Ortalamaları</div>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(perf.subject_averages).map(([subj, val]) => (
              <div key={subj} data-testid={`subject-bar-${subj}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{subj}</span>
                  <span className="font-mono">%{val}</span>
                </div>
                <div className="h-2 bg-muted rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-accent rounded transition-all duration-700"
                    style={{ width: `${val}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border font-display font-bold">Sınav Geçmişi</div>
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left p-3">Tarih</th><th className="text-left p-3">Doğru</th><th className="text-left p-3">Yanlış</th><th className="text-left p-3">Boş</th><th className="text-left p-3">Net</th></tr>
          </thead>
          <tbody>
            {sessions.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Henüz sınav çözmediniz.</td></tr>}
            {sessions.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="p-3">{s.submitted_at ? new Date(s.submitted_at).toLocaleString("tr-TR") : "-"}</td>
                <td className="p-3 text-success font-mono">{s.correct}</td>
                <td className="p-3 text-destructive font-mono">{s.incorrect}</td>
                <td className="p-3 text-muted-foreground font-mono">{s.unanswered}</td>
                <td className="p-3 font-mono font-bold">{(s.net_score || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const MiniStat = ({ icon, value, label }) => (
  <div className="p-3 rounded-xl bg-background/60 border border-border">
    <div className="text-muted-foreground mb-1">{icon}</div>
    <div className="text-2xl font-display font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);
